import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { backTargetFromLocation, withBackTarget } from "@/app/back-target";
import { caseDetailPath } from "@/app/routes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { isInteractiveTarget } from "@/lib/dom/is-interactive-target";
import { formatDateTime } from "@/lib/format/datetime";

import type { NotificationItem } from "./derive-notifications";
import { useNotifications } from "./use-notifications";

/**
 * 大項目4「通知」。人に気づかせるためのお知らせ一覧
 * （プロセスを動かす Messages とは別レイヤー）。
 *
 * MVP は既存データから導出し、既読は端末ごとの簡易実装。
 * Slack への送信は Service Task 側で行う。
 */
export function NotificationListPage() {
  const { t } = useTranslation("common");
  const { items, unreadCount, isPending, isError, markAllRead, markRead } =
    useNotifications();
  const navigate = useNavigate();
  const backTo = backTargetFromLocation(useLocation());

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">{t("notifications.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("notifications.description")}
          </p>
        </div>
        {unreadCount > 0 ? (
          <Button type="button" variant="outline" onClick={markAllRead}>
            {t("notifications.markAllRead")}
          </Button>
        ) : null}
      </header>

      {isPending ? <Skeleton className="h-32 w-full" /> : null}

      {isError ? (
        <p className="text-destructive text-sm">{t("state.error")}</p>
      ) : null}

      {!isPending && !isError ? (
        items.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("notifications.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onOpen={() => {
                  // 開いた通知はその場で既読にする（未読が残り続けないように）
                  markRead(item);
                  void navigate(
                    withBackTarget(
                      caseDetailPath(item.caseId, item.processModelId),
                      backTo,
                    ),
                  );
                }}
              />
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

function NotificationRow({
  item,
  onOpen,
}: {
  readonly item: NotificationItem;
  readonly onOpen: () => void;
}) {
  const { t } = useTranslation("common");

  return (
    <li
      // 通知全体をクリックで該当案件を開く。行内の操作要素は行の処理に流さない
      onClick={(event) => {
        if (isInteractiveTarget(event.target)) return;
        onOpen();
      }}
      className={`flex cursor-pointer flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm ${
        item.unread ? "border-primary/40 bg-primary/5" : "border-border"
      }`}
    >
      <Badge variant={item.kind === "returned" ? "destructive" : "secondary"}>
        {t(`notifications.kinds.${item.kind}`)}
      </Badge>
      {/* li は focus できないため、キーボード操作の受け口をボタンで用意する */}
      <button
        type="button"
        className="text-primary tabular-nums hover:underline"
        onClick={onOpen}
      >
        {item.caseNumber}
      </button>
      <span className="flex-1 font-medium">{item.title}</span>
      {item.applicant === null ? null : (
        <span className="text-muted-foreground text-xs">
          {t("notifications.fromApplicant", { name: item.applicant })}
        </span>
      )}
      <span className="text-muted-foreground text-xs tabular-nums">
        {formatDateTime(item.occurredAt)}
      </span>
      {item.unread ? (
        <span className="text-primary text-xs font-semibold">
          {t("notifications.unread")}
        </span>
      ) : null}
    </li>
  );
}
